import { useEffect, useRef } from 'react'

import { useAppDispatch } from 'src/Hooks/redux'
import { setPaymentAlertError } from 'src/Redux/payment-process'
import { PaymentErrors } from 'src/Components/PaymentErrorsAlert'
import {
  OrderCheckoutInfoResponse,
  OrderItemType,
  useAddOrderItemMutation,
  useCreateOrderMutation,
  useGetCurrentOrderQuery,
  useLazyGetCurrentOrderQuery,
  useLazyGetOrderCheckoutInfoQuery,
  useRemoveOrderItemsMutation,
  useUpdateOrderItemMutation,
} from 'src/Redux/artistOrderApi'
import { isOrderLocked } from 'src/Redux/artistOrderApi/isOrderLocked'

type UseOrderCheckoutInfoParams = {
  campaignId: number
  budget: number
  isCampaignFetching: boolean
}

export const useOrderCheckoutInfo = ({
  campaignId,
  budget,
  isCampaignFetching,
}: UseOrderCheckoutInfoParams):
  | OrderCheckoutInfoResponse
  | null
  | undefined => {
  const dispatch = useAppDispatch()

  const syncedOrderKeyRef = useRef<string | null>(null)

  const { data: currentOrder } = useGetCurrentOrderQuery()
  const isCurrentOrderLocked = isOrderLocked(currentOrder?.status)
  const [getCurrentOrder] = useLazyGetCurrentOrderQuery()
  const [createOrder] = useCreateOrderMutation()
  const [addOrderItem] = useAddOrderItemMutation()
  const [updateOrderItem] = useUpdateOrderItemMutation()
  const [removeOrderItems] = useRemoveOrderItemsMutation()
  const [triggerCheckoutInfo, { data: checkoutInfoData }] =
    useLazyGetOrderCheckoutInfoQuery()

  useEffect(() => {
    const syncKey = `${campaignId}:${budget}:${isCurrentOrderLocked}`

    if (
      !budget ||
      isCampaignFetching ||
      syncedOrderKeyRef.current === syncKey
    ) {
      return
    }
    syncedOrderKeyRef.current = syncKey

    const initializeOrder = async (): Promise<void> => {
      const latestOrder = await getCurrentOrder().unwrap()
      const order = latestOrder ?? (await createOrder().unwrap())

      if (isOrderLocked(order.status)) {
        return
      }

      const items = 'items' in order ? order.items : []

      const foreignItemIds = items
        .filter(
          (item) =>
            item.type !== OrderItemType.AdCampaign ||
            item.sourceId !== campaignId,
        )
        .map((item) => item.id)

      if (foreignItemIds.length) {
        await removeOrderItems({
          orderId: order.id,
          itemIds: foreignItemIds,
        }).unwrap()
      }

      const existingItem = items.find(
        (item) =>
          item.type === OrderItemType.AdCampaign &&
          item.sourceId === campaignId,
      )

      if (existingItem) {
        if (existingItem.fullPrice !== budget) {
          await updateOrderItem({
            orderId: order.id,
            itemId: existingItem.id,
            fullPrice: budget,
          }).unwrap()
        }
      } else {
        await addOrderItem({
          orderId: order.id,
          type: OrderItemType.AdCampaign,
          sourceId: campaignId,
          fullPrice: budget,
        }).unwrap()
      }

      await triggerCheckoutInfo({ coupons: [] }).unwrap()
    }

    initializeOrder().catch(() => {
      syncedOrderKeyRef.current = null
      dispatch(setPaymentAlertError(PaymentErrors.DEFAULT))
    })
  }, [
    addOrderItem,
    budget,
    campaignId,
    createOrder,
    dispatch,
    getCurrentOrder,
    isCampaignFetching,
    isCurrentOrderLocked,
    removeOrderItems,
    triggerCheckoutInfo,
    updateOrderItem,
  ])

  return checkoutInfoData
}
